import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { useSidebarLayout } from "@/hooks/useSidebarLayout";
import { useToast } from "@/components/ui/notification";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { 
  FileText, 
  Download, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Search,
  Filter,
  RefreshCw,
  Mail,
  Calendar,
  FileSize,
  Users,
  FolderTree,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileSpreadsheet,
  Trash2,
  AlertTriangle
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface Report {
  id: string;
  accProjectId: string;
  accHubId?: string;
  accAccountId?: string;
  title: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdBy: string;
  createdAt: string;
  completedAt?: string;
  fileCount: number;
  totalSize: number;
  downloadUrl?: string;
  downloadExpiry?: string;
  progress: number;
  errorMessage?: string;
  metadata?: any;
  emailSent: boolean;
  emailSentAt?: string;
}

type SortField = 'createdAt' | 'title' | 'status' | 'fileCount' | 'totalSize';
type SortDirection = 'asc' | 'desc';

export default function Reports() {
  const { projectId } = useParams<{ projectId: string }>();
  const { getPageHeaderClass } = useSidebarLayout();
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [reportToEmail, setReportToEmail] = useState<Report | null>(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Fetch reports
  const { data: reports = [], isLoading, refetch } = useQuery<Report[]>({
    queryKey: ["reports", projectId],
    queryFn: async () => {
      if (!projectId) return [];
      return await apiRequest(`/api/reports?projectId=${projectId}`);
    },
    enabled: !!projectId,
    refetchInterval: 10000, // Refresh every 10 seconds for real-time updates
  });

  // Sort and filter reports
  const filteredReports = reports
    .filter(report => {
      const matchesSearch = !searchTerm || 
        report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.createdBy.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || report.status === statusFilter;
      const matchesType = typeFilter === "all" || report.type === typeFilter;
      
      return matchesSearch && matchesStatus && matchesType;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'fileCount':
          aValue = a.fileCount;
          bValue = b.fileCount;
          break;
        case 'totalSize':
          aValue = a.totalSize;
          bValue = b.totalSize;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-blue-600" /> : 
      <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'processing':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      processing: "bg-blue-100 text-blue-800 border-blue-200",
      completed: "bg-green-100 text-green-800 border-green-200",
      failed: "bg-red-100 text-red-800 border-red-200"
    };
    
    return (
      <Badge variant="outline" className={variants[status as keyof typeof variants] || "bg-gray-100 text-gray-800"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'download':
        return <Download className="w-4 h-4" />;
      case 'file_log':
        return <FileSpreadsheet className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const handleDeleteClick = (report: Report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleSendEmail = (report: Report) => {
    setReportToEmail(report);
    setEmailAddress(''); // Reset email address
    setEmailDialogOpen(true);
  };

  const handleEmailSend = async () => {
    if (!reportToEmail || !emailAddress) return;
    
    setIsSendingEmail(true);
    try {
      const response = await apiRequest('/api/email/send', {
        method: 'POST',
        body: JSON.stringify({
          to: emailAddress,
          from: 'noreply@tectonixai.com',
          templateKey: 'download_ready',
          variables: {
            customerName: 'User', // You might want to get this from user context
            packageName: reportToEmail.title,
            downloadUrl: reportToEmail.downloadUrl,
            expiresAt: reportToEmail.downloadExpiry || '7 days'
          },
          // Add ACC information for tracking
          accAccountId: reportToEmail.accAccountId,
          accProjectId: reportToEmail.accProjectId,
          accHubId: reportToEmail.accHubId,
          downloadUrl: reportToEmail.downloadUrl,
          reportId: reportToEmail.id,
          userEmail: emailAddress,
          userName: 'User' // You might want to get this from user context
        })
      });

      if (response.ok) {
        toast.success('Email Sent Successfully', `Download link sent to ${emailAddress}`);
        setEmailDialogOpen(false);
        refetch(); // Refresh to update email status
      } else {
        const errorData = await response.json();
        toast.error('Email Send Failed', errorData.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Email Send Failed', error instanceof Error ? error.message : 'Please try again');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/reports/${reportToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        console.log('🗑️ Report deleted successfully');
        setDeleteDialogOpen(false);
        setReportToDelete(null);
        refetch(); // Refresh the reports list
      } else {
        const errorData = await response.json();
        alert(`Failed to delete report: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete report. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  const getDeleteDialogContent = (report: Report) => {
    const statusMessages = {
      pending: {
        title: "Delete Pending Report",
        description: "This report is waiting to be processed. Deleting it will remove it from the queue permanently.",
        warning: "The report has not started processing yet."
      },
      processing: {
        title: "Delete Processing Report", 
        description: "This report is currently being processed. Deleting it will stop the processing and remove all progress.",
        warning: "⚠️ This will cancel the ongoing processing and cannot be undone."
      },
      completed: {
        title: "Delete Completed Report",
        description: "This report has been successfully completed. Deleting it will remove the report and its download link.",
        warning: "The download link will no longer be accessible after deletion."
      },
      failed: {
        title: "Delete Failed Report",
        description: "This report failed to process. Deleting it will remove the error record and any partial data.",
        warning: "This will clean up the failed processing record."
      }
    };

    return statusMessages[report.status] || statusMessages.failed;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className={getPageHeaderClass()}>
          <div className="h-full flex items-center px-6">
            <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          </div>
        </div>
        <main className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 max-w-full mx-auto">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Page Header */}
      <div className={getPageHeaderClass()}>
        <div className="h-full flex items-center justify-between px-6">
          <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          <Button 
            onClick={async () => {
              console.log('🔄 Manual refresh triggered');
              setIsRefreshing(true);
              try {
                await refetch();
                // 即使数据没有变化，也显示刷新完成
                setTimeout(() => {
                  setIsRefreshing(false);
                }, 500);
              } catch (error) {
                console.error('Refresh error:', error);
                setIsRefreshing(false);
              }
            }} 
            variant="outline" 
            size="sm"
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>
      
      {/* Main Content */}
        <main className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 max-w-full mx-auto space-y-6">
          <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Background Reports
              </CardTitle>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <div className="relative min-w-[200px]">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="download">Download</SelectItem>
                    <SelectItem value="export">Export</SelectItem>
                    <SelectItem value="file_log">File Log</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {filteredReports.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reports</h3>
                <p className="text-gray-500">
                  {searchTerm || statusFilter !== "all" || typeFilter !== "all" 
                    ? "No reports match your current filters."
                    : "No background reports have been created yet."
                  }
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('title')}
                      >
                        <div className="flex items-center gap-2">
                          Title
                          {getSortIcon('title')}
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Type</TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Status
                          {getSortIcon('status')}
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Progress</TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('fileCount')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Files
                          {getSortIcon('fileCount')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('totalSize')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Size
                          {getSortIcon('totalSize')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Created
                          {getSortIcon('createdAt')}
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>
                          {getStatusIcon(report.status)}
                        </TableCell>
                        
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900">
                              {report.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              by {report.createdBy}
                            </div>
                          </div>
                        </TableCell>
                        
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            {getTypeIcon(report.type)}
                            <span className="capitalize">
                              {report.type === 'file_log' ? 'File Log' : report.type}
                            </span>
                          </div>
                        </TableCell>
                        
                        <TableCell className="text-center">
                          {getStatusBadge(report.status)}
                        </TableCell>
                        
                        <TableCell className="text-center">
                          {report.status === 'processing' ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                  style={{ width: `${report.progress}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-600">{report.progress}%</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        
                        <TableCell className="text-center">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            {report.fileCount}
                          </Badge>
                        </TableCell>
                        
                        <TableCell className="text-center">
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">
                            {formatFileSize(report.totalSize)}
                          </Badge>
                        </TableCell>
                        
                        <TableCell className="text-center">
                          <div className="text-sm text-gray-600">
                            {(() => {
                              const createdAt = new Date(report.createdAt);
                              const now = new Date();
                              const daysDiff = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
                              
                              if (daysDiff <= 3) {
                                return formatDistanceToNow(createdAt, { addSuffix: true });
                              } else {
                                return createdAt.toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                });
                              }
                            })()}
                          </div>
                        </TableCell>
                        
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            {report.status === 'completed' && report.downloadUrl && (
                              (() => {
                                const isExpired = report.downloadExpiry && new Date() > new Date(report.downloadExpiry);
                                return isExpired ? (
                                  <div 
                                    className="flex items-center justify-center h-8 w-8 rounded-md bg-gray-100 text-gray-400 border border-gray-200"
                                    title="Download Link Expired, only valid for 7 days"
                                  >
                                    <Download className="w-4 h-4" />
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      try {
                                        const downloadWindow = window.open(report.downloadUrl, '_blank');
                                        if (!downloadWindow) {
                                          alert('Please allow popups for this site to download files');
                                        }
                                      } catch (error) {
                                        console.error('Download error:', error);
                                        alert('Failed to open download link. Please try again.');
                                      }
                                    }}
                                    className="h-8 w-8 p-0 text-green-600 border-green-200 hover:bg-green-50"
                                    title="Download"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                );
                              })()
                            )}
                            
                            {report.status === 'failed' && (
                              <div 
                                className="flex items-center justify-center h-8 w-8 rounded-md bg-red-50 text-red-700 border border-red-200"
                                title={`Failed: ${report.errorMessage || 'Unknown error'}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </div>
                            )}
                            
                            {/* All status reports can be deleted */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteClick(report)}
                              className="h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50"
                              title="Delete report"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            
                            {/* Email button - show for completed reports */}
                            {report.status === 'completed' && report.downloadUrl && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSendEmail(report)}
                                className={`h-8 w-8 p-0 ${
                                  report.emailSent 
                                    ? 'text-green-600 border-green-200 bg-green-50' 
                                    : 'text-blue-600 border-blue-200 hover:bg-blue-50'
                                }`}
                                title={report.emailSent ? "Email already sent" : "Send download email"}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Email Send Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Send Download Email
            </DialogTitle>
            <DialogDescription>
              Send a download notification email for "{reportToEmail?.title}"
            </DialogDescription>
          </DialogHeader>
          
          {reportToEmail && (
            <div className="py-4">
              <div className="space-y-4">
                <div>
                  <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Email Address
                  </label>
                  <Input
                    id="email-address"
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full"
                  />
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-600">
                    <div className="font-medium mb-1">Email Preview:</div>
                    <div className="text-xs">
                      <div><strong>To:</strong> {emailAddress || 'user@example.com'}</div>
                      <div><strong>Subject:</strong> Tectonix - Your download is ready: {reportToEmail.title}</div>
                      <div><strong>Template:</strong> download_ready</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialogOpen(false)}
              disabled={isSendingEmail}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEmailSend}
              disabled={!emailAddress || isSendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {reportToDelete ? getDeleteDialogContent(reportToDelete).title : "Delete Report"}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {reportToDelete ? getDeleteDialogContent(reportToDelete).description : ""}
            </DialogDescription>
          </DialogHeader>
          
          {reportToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Report:</span>
                  <span className="text-gray-700">{reportToDelete.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Status:</span>
                  {getStatusBadge(reportToDelete.status)}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Created:</span>
                  <span className="text-gray-700">
                    {formatDistanceToNow(new Date(reportToDelete.createdAt), { addSuffix: true })}
                  </span>
                </div>
                {reportToDelete.fileCount > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Files:</span>
                    <span className="text-gray-700">{reportToDelete.fileCount} files</span>
                  </div>
                )}
              </div>
              
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-yellow-800">
                    {getDeleteDialogContent(reportToDelete).warning}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="flex items-center gap-2"
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
